import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
    Video,
    Users,
    Clock,
    TrendingUp,
    Play,
    Calendar,
    Plus,
    ArrowRight,
    Radio
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Dashboard = () => {
    const navigate = useNavigate();
    const [analytics, setAnalytics] = useState(null);
    const [recentStreams, setRecentStreams] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [analyticsRes, streamsRes] = await Promise.all([
                    axios.get(`${API}/analytics`),
                    axios.get(`${API}/streams?limit=5`)
                ]);
                setAnalytics(analyticsRes.data);
                setRecentStreams(streamsRes.data);
            } catch (error) {
                console.error('Error fetching dashboard data:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    const formatDuration = (seconds) => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        if (hours > 0) return `${hours}h ${minutes}m`;
        return `${minutes}m`;
    };

    const getStatusBadge = (status) => {
        const styles = {
            live: 'status-badge status-live',
            scheduled: 'status-badge status-scheduled',
            ended: 'status-badge status-ended',
            draft: 'status-badge status-draft'
        };
        return styles[status] || styles.draft;
    };

    const stats = analytics ? [
        { label: 'Total Streams', value: analytics.total_streams, icon: Video, color: 'text-primary' },
        { label: 'Total Viewers', value: analytics.total_viewers.toLocaleString(), icon: Users, color: 'text-blue-500' },
        { label: 'Watch Time', value: `${analytics.total_watch_time_hours}h`, icon: Clock, color: 'text-green-500' },
        { label: 'Growth', value: `+${analytics.growth_percentage}%`, icon: TrendingUp, color: 'text-purple-500' },
    ] : [];

    return (
        <div className="space-y-6" data-testid="dashboard-page">
            {/* Welcome Section */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl lg:text-3xl font-bold">Welcome back, John!</h1>
                    <p className="text-muted-foreground mt-1">Here's what's happening with your streams</p>
                </div>
                <Button
                    size="lg"
                    onClick={() => navigate('/studio')}
                    className="bg-primary hover:bg-primary/90 gap-2"
                    data-testid="create-stream-btn"
                >
                    <Plus className="w-5 h-5" />
                    New Stream
                </Button>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {stats.map((stat, index) => (
                    <Card key={index} className="card-stat" data-testid={`stat-${stat.label.toLowerCase().replace(' ', '-')}`}>
                        <CardContent className="p-4 lg:p-6">
                            <div className="flex items-start justify-between">
                                <div>
                                    <p className="text-sm text-muted-foreground">{stat.label}</p>
                                    <p className="text-2xl lg:text-3xl font-bold mt-1">{stat.value}</p>
                                </div>
                                <div className={`p-2 rounded-lg bg-muted ${stat.color}`}>
                                    <stat.icon className="w-5 h-5" />
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card
                    className="cursor-pointer hover:shadow-lg transition-all group"
                    onClick={() => navigate('/studio')}
                    data-testid="quick-action-golive"
                >
                    <CardContent className="p-6 flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-red-500/10 flex items-center justify-center group-hover:bg-red-500/20 transition-colors">
                            <Radio className="w-6 h-6 text-red-500" />
                        </div>
                        <div className="flex-1">
                            <h3 className="font-semibold">Go Live Now</h3>
                            <p className="text-sm text-muted-foreground">Start streaming instantly</p>
                        </div>
                        <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:translate-x-1 transition-transform" />
                    </CardContent>
                </Card>

                <Card
                    className="cursor-pointer hover:shadow-lg transition-all group"
                    onClick={() => navigate('/studio')}
                    data-testid="quick-action-schedule"
                >
                    <CardContent className="p-6 flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center group-hover:bg-blue-500/20 transition-colors">
                            <Calendar className="w-6 h-6 text-blue-500" />
                        </div>
                        <div className="flex-1">
                            <h3 className="font-semibold">Schedule Stream</h3>
                            <p className="text-sm text-muted-foreground">Plan your next broadcast</p>
                        </div>
                        <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:translate-x-1 transition-transform" />
                    </CardContent>
                </Card>

                <Card
                    className="cursor-pointer hover:shadow-lg transition-all group"
                    onClick={() => navigate('/broadcasts')}
                    data-testid="quick-action-destinations"
                >
                    <CardContent className="p-6 flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center group-hover:bg-purple-500/20 transition-colors">
                            <Video className="w-6 h-6 text-purple-500" />
                        </div>
                        <div className="flex-1">
                            <h3 className="font-semibold">Manage Destinations</h3>
                            <p className="text-sm text-muted-foreground">Connect your platforms</p>
                        </div>
                        <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:translate-x-1 transition-transform" />
                    </CardContent>
                </Card>
            </div>

            {/* Recent Streams */}
            <Card data-testid="recent-streams">
                <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle>Recent Streams</CardTitle>
                    <Button variant="ghost" size="sm" onClick={() => navigate('/recordings')}>
                        View All
                        <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                </CardHeader>
                <CardContent>
                    {loading ? (
                        <div className="space-y-4">
                            {[1, 2, 3].map((i) => (
                                <div key={i} className="h-20 bg-muted rounded-lg animate-pulse" />
                            ))}
                        </div>
                    ) : recentStreams.length === 0 ? (
                        <div className="text-center py-8">
                            <Video className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                            <p className="text-muted-foreground">No streams yet</p>
                            <Button
                                variant="outline"
                                className="mt-4"
                                onClick={() => navigate('/studio')}
                            >
                                Create your first stream
                            </Button>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {recentStreams.map((stream) => (
                                <div
                                    key={stream.id}
                                    className="flex items-center gap-4 p-3 rounded-xl hover:bg-muted/50 transition-colors cursor-pointer"
                                    onClick={() => stream.status === 'live' || stream.status === 'scheduled' ? navigate(`/studio/${stream.id}`) : null}
                                    data-testid={`stream-item-${stream.id}`}
                                >
                                    <div className="w-24 h-14 rounded-lg bg-muted overflow-hidden flex-shrink-0">
                                        {stream.thumbnail_url ? (
                                            <img
                                                src={stream.thumbnail_url}
                                                alt={stream.title}
                                                className="w-full h-full object-cover"
                                            />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center">
                                                <Video className="w-6 h-6 text-muted-foreground" />
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <h4 className="font-medium truncate">{stream.title}</h4>
                                        <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
                                            <span className={getStatusBadge(stream.status)}>
                                                {stream.status}
                                            </span>
                                            {stream.duration_seconds > 0 && (
                                                <span className="flex items-center gap-1">
                                                    <Clock className="w-3 h-3" />
                                                    {formatDuration(stream.duration_seconds)}
                                                </span>
                                            )}
                                            {stream.viewer_count > 0 && (
                                                <span className="flex items-center gap-1">
                                                    <Users className="w-3 h-3" />
                                                    {stream.viewer_count.toLocaleString()}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    {(stream.status === 'live' || stream.status === 'scheduled') && (
                                        <Button size="sm" variant="outline">
                                            <Play className="w-4 h-4 mr-1" />
                                            {stream.status === 'live' ? 'Join' : 'Edit'}
                                        </Button>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export default Dashboard;
